
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Pencil } from "lucide-react";
import { ContactForm } from "./ContactForm";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContactDetailsView } from "./ContactDetailsView";
import { useAuth } from "@/contexts/AuthContext";

interface ContactDetailsDialogProps {
  contact: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ContactDetailsDialog = ({ contact, open, onOpenChange }: ContactDetailsDialogProps) => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [currentContact, setCurrentContact] = useState(contact);
  const queryClient = useQueryClient();
  const { session } = useAuth();

  useEffect(() => {
    setCurrentContact(contact);
  }, [contact]);

  // Reset editing state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
    }
  }, [open]);

  useEffect(() => {
    if (!contact || !session) return;

    const channel = supabase
      .channel(`contact-${contact.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contacts',
          filter: `id=eq.${contact.id}`
        },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            onOpenChange(false);
            return;
          }
          setCurrentContact(payload.new);
          queryClient.invalidateQueries({ queryKey: ["contacts"] });
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [contact, onOpenChange, session, queryClient]);

  const handleEditSuccess = () => {
    setIsEditing(false);
    queryClient.invalidateQueries({ queryKey: ["contacts"] });
    toast({
      title: "Success",
      description: "Contact updated successfully",
    });
  };

  if (!currentContact || !session) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <DialogTitle>{isEditing ? "Edit Contact" : "Contact Details"}</DialogTitle>
            {!isEditing && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto px-6">
          {isEditing ? (
            <ContactForm 
              initialData={currentContact}
              onSuccess={handleEditSuccess}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <ContactDetailsView contact={currentContact} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
